import MUIContainer from '@/components/MUI-Components/MUI-Container';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import React, {memo} from 'react';
import {CommonImageWrapper} from '../home-services';
import CommonCard from '../common-card';
import DisplayImage from '../../display-image';
import Eyebrow from '@/components/Common/Eyebrow';
import {fadeInUp} from '@/animation/scroll-animation';
import {AnimatedComponent} from '../../AnimatedComponent';
import Link from 'next/link';
import parse from 'html-react-parser';
import {borderRadius, colors, fontSize, fontWeight} from '@/styles/tokens';
import {ICON_TILE_ICON_SIZE, iconTileImageStyle} from '@/styles/iconTile';
import {sectionHeadingSx} from '@/styles/sectionHeading';

const bulletListSx = {listStyle: 'none', p: 0, m: 0, mt: 2} as const;

const bestSuitedSx = {
    mt: 'auto',
    mx: -3,
    mb: -3,
    pt: 2,
    px: 3,
    pb: 2.5,
    borderRadius: '0 0 18px 18px',
    bgcolor: colors.background.tag
} as const;

const bestSuitedTextSx = {
    fontSize: fontSize[14],
    color: colors.primary.main,
    lineHeight: 1.5
} as const;

const HomeBusiness = (props: any) => {
    const {heading, boldSubheading, subheading, contentData, md, eyebrow} = props;
    return (
        <Box>
            <MUIContainer>
                <Box textAlign="center" maxWidth={1024} margin="0 auto" mb={5}>
                    {eyebrow && <Eyebrow text={eyebrow} sx={{textAlign: 'center'}} />}
                    <Typography variant="h1" component="h2" mb={3} className="font-headers" sx={sectionHeadingSx}>
                        {heading}
                    </Typography>
                    {boldSubheading && (
                        <Typography variant="h3" component="p" mb={2} fontSize={{xs: 16, sm: 22}} className="font-headers" fontWeight={700}>
                            {boldSubheading}
                        </Typography>
                    )}
                    <Typography variant="subtitle1" component="p" className="font-regular-text generative-transforming-subheading">
                        {subheading ? parse(subheading) : ''}
                    </Typography>
                </Box>
                <Box>
                    <Grid container spacing={3} rowSpacing={5}>
                        {contentData?.map((item: any, index: number) => (
                            <Grid item xs={12} sm={6} md={md ? 4 : 6} key={index + 1}>
                                <AnimatedComponent variants={fadeInUp} delay={index * 0.2} className="height-full">
                                    <CommonCard blueHover>
                                        <Box display="flex" gap={3} pb={2} alignItems="center">
                                            <CommonImageWrapper>
                                                {item.icon ? (
                                                    item.icon
                                                ) : (
                                                    <DisplayImage
                                                        alt={item.heading}
                                                        src={item.src}
                                                        className="home-service-icon"
                                                        width={ICON_TILE_ICON_SIZE}
                                                        height={ICON_TILE_ICON_SIZE}
                                                        style={iconTileImageStyle}
                                                    />
                                                )}
                                            </CommonImageWrapper>
                                            {item.redirect ? (
                                                <Typography variant="h3" component="h3" mt={2} mb={2}>
                                                    <Link
                                                        target="_blank"
                                                        href={item.redirect}
                                                        className="card-redirection-heading font-headers "
                                                    >
                                                        {item.heading}
                                                    </Link>
                                                </Typography>
                                            ) : (
                                                <Typography variant="h3" component="h3" mt={2} mb={2} className="font-headers">
                                                    {item.heading}
                                                </Typography>
                                            )}
                                        </Box>

                                        <Typography
                                            variant="body1"
                                            component="p"
                                            className="font-regular-text generative-transforming-subheading"
                                        >
                                            {item.body ? parse(item.body) : ''}
                                        </Typography>
                                        {item.bullets?.length > 0 && (
                                            <Box component="ul" sx={bulletListSx}>
                                                {item.bullets.map((bullet: string, i: number) => (
                                                    <Box
                                                        key={i}
                                                        component="li"
                                                        sx={{display: 'flex', alignItems: 'center', gap: 1.5, mb: 1}}
                                                    >
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                width: 14,
                                                                height: 14,
                                                                minWidth: 14,
                                                                bgcolor: '#006D3C',
                                                                borderRadius: borderRadius.circle,
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                flexShrink: 0,
                                                                color: colors.text.inverse,
                                                                fontSize: fontSize[12],
                                                                fontWeight: fontWeight.bold
                                                            }}
                                                        >
                                                            ✓
                                                        </Box>
                                                        <Typography
                                                            variant="body2"
                                                            className="font-regular-text"
                                                            sx={{color: colors.text.primary, fontSize: 16}}
                                                        >
                                                            {parse(bullet)}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                        {item.bestSuitedFor && (
                                            <Box className="card-tinted-footer" sx={bestSuitedSx}>
                                                <Typography component="p" className="font-regular-text" sx={bestSuitedTextSx}>
                                                    {parse(item.bestSuitedFor)}
                                                </Typography>
                                            </Box>
                                        )}
                                    </CommonCard>
                                </AnimatedComponent>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </MUIContainer>
        </Box>
    );
};

export default memo(HomeBusiness);
